import { useEffect } from "react";
import { Sparkles } from "lucide-react";
import { Reveal, ChapterLabel } from "@/components/Reveal";
import { PRIVATE_EXPERIENCES_EMAIL } from "@/lib/site";
import { useLang } from "@/i18n";
import { IMAGES } from "@/content/images";

const SoundHealing = () => {
  const { t } = useLang();
  const d = t.sound;

  useEffect(() => {
    window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="sound-healing-page" className="bg-ivory">
      <section className="relative flex h-[72vh] min-h-[540px] items-end overflow-hidden">
        <img src={IMAGES.soundHealing.hero} alt="Therapeutic sound healing with crystal singing bowls in Cabo San Lucas"
          data-testid="sound-hero-image" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-charcoal/10" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 md:px-12 pb-14 md:pb-20">
          <Reveal><p className="label-caps text-gold">{d.heroLabel}</p></Reveal>
          <Reveal delay={0.1}>
            <h1 data-testid="sound-hero-headline"
              className="mt-6 font-display text-ivory text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] tracking-tight">
              {d.h1a} <span className="italic font-light">{d.h1b}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-sm md:text-base leading-relaxed text-ivory/85">{d.heroSub}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 py-28 md:py-40">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-6">
            <ChapterLabel index={d.introChapter} title={d.introLabel} />
            <Reveal delay={0.1}>
              <h2 data-testid="sound-intro-headline"
                className="mt-10 font-display text-charcoal text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-tight">
                {d.introHeadingA} <span className="italic font-light">{d.introHeadingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:col-start-8 flex flex-col justify-end gap-6">
            <Reveal delay={0.15}><p className="text-base md:text-lg leading-relaxed text-charcoal/70">{d.introP1}</p></Reveal>
            <Reveal delay={0.22}><p className="text-base leading-relaxed text-charcoal/60">{d.introP2}</p></Reveal>
          </div>
        </div>
      </section>

      <section data-testid="sound-provided-section" className="bg-sand px-6 md:px-12 py-24 md:py-36">
        <div className="mx-auto max-w-[1600px]">
          <div className="max-w-2xl">
            <ChapterLabel index={d.providedChapter} title={d.providedLabel} />
            <Reveal delay={0.1}>
              <h2 className="mt-10 font-display text-charcoal text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                {d.providedHeadingA} <span className="italic font-light">{d.providedHeadingB}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}><p className="mt-6 text-base leading-relaxed text-charcoal/65">{d.providedCopy}</p></Reveal>
          </div>
          <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {d.provided.map((p, i) => (
              <Reveal key={p.title} delay={0.08 * i} y={30}>
                <div data-testid={`sound-provided-0${i + 1}`} className="border-t border-charcoal/15 pt-6">
                  <p className="label-caps text-gold">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-2xl text-charcoal">{p.title}</h3>
                  {p.text && <p className="mt-3 text-sm leading-relaxed text-charcoal/60">{p.text}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="sound-menu-section" className="bg-espresso px-6 py-28 text-ivory md:px-12 md:py-40">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <ChapterLabel index={d.menuChapter} title={d.menuLabel} light />
            <Reveal delay={0.1}>
              <h2 data-testid="sound-menu-headline"
                className="mt-10 font-display text-4xl leading-[1.02] tracking-tight text-ivory sm:text-5xl">
                {d.menuHeadingA} <span className="italic font-light">{d.menuHeadingB}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}><p data-testid="sound-studio-exclusive" className="mt-7 inline-flex max-w-sm border border-gold/70 bg-gold/15 px-4 py-3 text-sm font-semibold leading-relaxed text-ivory shadow-[0_10px_30px_rgba(0,0,0,0.16)]">{d.menuIntro}</p></Reveal>
            <Reveal delay={0.18}><p className="mt-7 label-caps text-ivory/50">{d.menuNote}</p></Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal delay={0.15}>
              <ul data-testid="sound-menu-list" className="divide-y divide-ivory/20 border-y border-ivory/20">
                {d.menu.map((m, i) => (
                  <li key={m.name} data-testid={`sound-menu-item-${i + 1}`}
                    className="group flex items-baseline justify-between gap-6 py-6 md:py-7">
                    <span className="font-display text-2xl leading-snug text-ivory md:text-[1.8rem]">{m.name}{m.note && <span className="mt-3 block max-w-xl font-sans text-sm leading-relaxed text-ivory/70">{m.note}</span>}</span>
                    <span className="shrink-0 label-caps text-gold">{m.price}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.22}><p className="mt-6 max-w-2xl text-sm leading-relaxed text-ivory/65">{d.menuTerms}</p></Reveal>
          </div>
        </div>
      </section>

      <section data-testid="sound-on-location-section" className="bg-sand/55 px-6 md:px-12 py-28 md:py-40">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 md:grid-cols-12 md:gap-12">
          <Reveal className="md:col-span-5" y={40}>
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.soundHealing.portraitTwo} alt="Therapeutic crystal singing bowls prepared for a private experience by the sea in Los Cabos" loading="lazy" className="h-full w-full object-cover object-[center_58%]" />
            </div>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <ChapterLabel index={d.onLocation.chapter} title={d.onLocation.label} />
            <Reveal delay={0.1}>
              <h2 className="mt-10 font-display text-charcoal text-4xl sm:text-5xl leading-[1.02] tracking-tight">
                {d.onLocation.headingA} <span className="italic font-light">{d.onLocation.headingB}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}><p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/65">{d.onLocation.copy}</p></Reveal>
            <Reveal delay={0.2}><p className="mt-10 label-caps text-charcoal/40">{d.onLocation.menuNote}</p></Reveal>
            <Reveal delay={0.24}>
              <ul data-testid="sound-on-location-menu-list" className="mt-5 divide-y divide-charcoal/12 border-y border-charcoal/12">
                {d.onLocation.menu.map((m, i) => (
                  <li key={m.name} data-testid={`sound-on-location-menu-item-${i + 1}`} className="flex items-baseline justify-between gap-6 py-6 md:py-7">
                    <span className="font-display text-2xl leading-snug text-charcoal md:text-[1.8rem]">{m.name}</span>
                    <span className="shrink-0 label-caps text-charcoal/70">{m.price}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="sound-gong-section" className="bg-lavender/40 px-6 md:px-12 py-24 md:py-36">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-12 md:grid-cols-12 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="flex items-center gap-4">
                <Sparkles size={22} strokeWidth={1} className="text-gold" />
                <p className="label-caps text-charcoal/50">{d.gongLabel}</p>
              </div>
            </Reveal>
            <Reveal delay={0.05}><p className="mt-4 label-caps text-charcoal/40">{d.gongAvailability}</p></Reveal>
            <Reveal delay={0.1}>
              <h2 data-testid="sound-gong-headline"
                className="mt-8 font-display text-charcoal text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.02] tracking-tight">
                {d.gongHeadingA} <span className="italic font-light">{d.gongHeadingB}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}><p className="mt-6 font-display text-2xl text-espresso">{d.gongPrice}</p></Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-16">
            <Reveal delay={0.15}><p className="text-base md:text-lg leading-relaxed text-charcoal/70">{d.gongCopy}</p></Reveal>
          </div>
        </div>
      </section>

      <section data-testid="sound-cta-section" className="bg-espresso px-6 md:px-12 py-28 md:py-40 text-ivory">
        <div className="mx-auto max-w-[1600px]">
          <Reveal><p className="label-caps text-ivory/50">{d.ctaLabel}</p></Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="sound-cta-headline"
              className="mt-8 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              {d.ctaHeadingA} <span className="italic font-light text-sand">{d.ctaHeadingB}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <a href={`mailto:${PRIVATE_EXPERIENCES_EMAIL}?subject=Private%20Sound%20Healing%20Experience%20Enquiry`} data-testid="sound-book-button" className="btn-pill mt-12 bg-ivory text-charcoal hover:bg-sand">
              {d.cta}
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default SoundHealing;
